<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { BookOpen, FileArchive, FolderPlus } from '@lucide/vue'
import { Button, Checkbox, CheckboxField, CheckboxGroup, Description, InlineAlert, Label, Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions, StateCard, useToast } from 'catalyst-vue'
import { useLibraryStore } from '../../stores/library'
import LibraryOverlay from './library-overlay.vue'
import { errorMessage, type Candidate } from '../../lib/fs-access'
import { useControlSize } from '../../lib/library-layout'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: [] }>()
const store = useLibraryStore()
const toast = useToast()
const controlSize = useControlSize()
const sourceId = ref('')
const items = ref<Candidate[]>([])
const truncated = ref(false)
const selected = ref<string[]>([])
const loading = ref(false)
const saving = ref(false)
const error = ref('')
const addable = computed(() => items.value.filter(item => !item.added && item.pageCount > 0 && !item.error))
const source = computed(() => store.roots.find(root => root.id === sourceId.value))
const sourceAuthorized = computed(() => !!source.value && store.isAuthorized(source.value.id))
let generation = 0

function statusOf(item: Candidate): string {
  if (item.error) return item.error
  if (item.added) return '已加入書庫'
  if (!item.pageCount) return item.kind === 'archive' ? '不可加入 · 壓縮檔內沒有圖片' : '不可加入 · 沒有圖片'
  return `${item.pageCount} 頁`
}

async function loadCandidates() {
  const current = ++generation
  selected.value = []
  items.value = []
  truncated.value = false
  error.value = ''
  if (!sourceId.value) return
  loading.value = true
  try {
    const result = await store.candidates(sourceId.value)
    if (current === generation) { items.value = result.candidates; truncated.value = result.truncated }
  } catch (cause) { if (current === generation) error.value = errorMessage(cause) }
  finally { if (current === generation) loading.value = false }
}

watch(() => props.open, async open => {
  if (!open) return
  if (!store.roots.some(root => root.id === sourceId.value)) sourceId.value = store.roots[0]?.id ?? ''
  await loadCandidates()
})
// 切換來源時捨棄前一個來源的未確認勾選。
watch(sourceId, () => { if (props.open) void loadCandidates() })

function toggle(relPath: string, checked: boolean) {
  selected.value = checked ? [...selected.value, relPath] : selected.value.filter(value => value !== relPath)
}

async function addRoot() {
  const result = await store.addRoot()
  if (result === 'duplicate') { toast.warning('這個資料夾已經是書庫的來源了', { description: '沒有建立第二筆來源。' }); return }
  if (!result) return
  toast.success(`已加入根資料夾「${result.name}」`)
  sourceId.value = result.id
}

async function importComic() {
  const result = await store.importComicFolder()
  if (result === 'duplicate') { toast.warning('這個資料夾已經是書庫的來源了', { description: '沒有建立第二筆來源，也沒有重複加入作品。' }); return }
  if (result === 'empty') { toast.error('這個資料夾裡沒有圖片檔', { description: '沒有建立來源也沒有加入作品。作品資料夾需要直接包含圖片。' }); return }
  if (!result) return
  toast.success(`已匯入「${result.title}」`)
  emit('close')
}

async function save() {
  if (saving.value || !selected.value.length || !sourceId.value) return
  saving.value = true
  error.value = ''
  try { await store.addSelected(sourceId.value, selected.value); emit('close') }
  catch (cause) { error.value = errorMessage(cause) }
  finally { saving.value = false }
}
function close() { if (!saving.value) emit('close') }
</script>

<template>
  <LibraryOverlay
    :open="open"
    size="2xl"
    title="加入作品"
    description="從根資料夾勾選要加入的作品，或直接把單獨一本作品的資料夾匯入。只讀取，不移動原始檔案。"
    :pending="saving"
    @close="close"
  >
    <div class="add-source-row">
      <Listbox v-if="store.roots.length" v-model="sourceId" :disabled="saving">
        <ListboxButton class="add-source-select" :size="controlSize" aria-label="要從哪一個根資料夾列出候選" />
        <ListboxOptions>
          <ListboxOption v-for="root in store.roots" :key="root.id" :value="root.id">
            <ListboxLabel>{{ root.name }}</ListboxLabel>
          </ListboxOption>
        </ListboxOptions>
      </Listbox>
      <div class="add-source-actions">
        <Button outline :size="controlSize" :disabled="saving || store.busy || !store.supported" @click="addRoot">
          <FolderPlus :size="16" aria-hidden="true" />選擇漫畫根目錄
        </Button>
        <Button outline :size="controlSize" :disabled="saving || store.busy || !store.supported" @click="importComic">
          <BookOpen :size="16" aria-hidden="true" />直接匯入作品資料夾
        </Button>
      </div>
    </div>

    <InlineAlert v-if="error" tone="error" class="mt-4">{{ error }}</InlineAlert>
    <StateCard v-if="loading" title="正在查看資料夾" message="往下尋找含有圖片的資料夾與壓縮檔…" :actionable="false" class="mt-4" />
    <template v-else>
      <StateCard v-if="!store.roots.length" title="還沒有根資料夾" message="先用「選擇漫畫根目錄」加入一個，或用「直接匯入作品資料夾」加入單獨一本作品。" :actionable="false" class="mt-4" />
      <StateCard v-else-if="!sourceAuthorized" title="這個來源尚未開啟" message="回到書庫先開啟這個來源的資料夾權限，才能列出它底下的候選。" :actionable="false" class="mt-4" />
      <StateCard v-else-if="!addable.length && !error" title="目前沒有可加入的作品" message="請在根資料夾下放入含圖片的資料夾或 zip／cbz 壓縮檔；已在書庫的作品不會重複加入。" :actionable="false" class="mt-4" />
      <InlineAlert v-if="truncated" tone="warning" class="mt-4">有些分支太深沒有展開（最多往下四層）。若作品在更深的層級，請改選比較接近的資料夾當根目錄。</InlineAlert>
      <CheckboxGroup v-if="items.length" class="mt-5">
        <CheckboxField v-for="item in items" :key="item.relPath" :disabled="saving || item.added || !item.pageCount || !!item.error" class="candidate-row">
          <Checkbox color="primary" :aria-label="item.relPath" :model-value="selected.includes(item.relPath)" @update:model-value="toggle(item.relPath, $event)" />
          <Label class="break-words candidate-label">
            <FileArchive v-if="item.kind === 'archive'" :size="14" aria-hidden="true" />{{ item.relPath }}
          </Label>
          <Description>{{ statusOf(item) }}</Description>
        </CheckboxField>
      </CheckboxGroup>
    </template>
    <template #actions>
      <Button outline :size="controlSize" :disabled="saving" @click="close">取消</Button>
      <Button color="primary" :size="controlSize" :disabled="loading || saving || !selected.length" @click="save">{{ saving ? '正在加入…' : `加入 ${selected.length} 本作品` }}</Button>
    </template>
  </LibraryOverlay>
</template>
